// @flow
/* -----------------Globals--------------- */
import React, {
  useState,
} from 'react';

/* -----------------Third parties--------------- */
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import {
  useRouter,
} from 'next/router';

/* -----------------UI--------------- */
import {
  Fab,
  Container,
  Paper,
  CircularProgress,
  Grid,
  useMediaQuery,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

/* -----------------Icons--------------- */
import {
  CardsOutline,
  Checkerboard,
  FormatListChecks,
  Play,
} from 'mdi-material-ui';

import {
  APP_CONTENT_HEADER_MIN_WIDTH,
} from './constants';

/* -----------------Types--------------- */
import type {
  Content,
  ContentMetaData,
  FolderTypes,
  ContentVariants,
} from 'common/types';
import type {
  ContentInputs,
} from 'common/session/types';
import {
  accessType as contentAccessType,
  learningMode,
} from 'common/types';
import type {
  TBreadcrumbs,
} from './appbarutils';

import {
  isEmpty,
} from 'common/simpleUtils';
import {
  getItemsCount,
  getContentTitleAndDescription,
} from 'common/content/appcontentutils';
import {
  authenticated,
  key,
} from '../../common/authutils';
import {
  getContentItemsCount,
} from 'common/utils';
import {
  getContentIconUrl,
} from 'common/content/iconutils';

/* ----------------- Queries --------------- */
import fetchPremiumContentDetails from 'common/store/queries/fetchPremiumContentDetails';

/* ----------------- Configs --------------- */
import env from '@components/env/relay-environment-web';

/* -----------------Child components--------------- */
import Appbar, {
} from './Appbar';
import LearningContentHero from './LearningContentHero';
import { trackEvent } from '../../analytics/tracking';

/* -----------------Lazy components--------------- */
const ContentInfoDialog = dynamic(() => import('./dialogs/DialogInfo'), { ssr: false });
const DialogAuth = dynamic(() => import('../auth/DialogAuth'), { ssr: false });
const BannerPremiumContainer = dynamic(() => import('./BannerPremiumContainer'), { ssr: false });
const StudySessionUnAuthLoader = dynamic(() => import('@components/loading/StudySessionUnAuthLoader'), { ssr: false });

/* -----------------Styles--------------- */
const styles = theme => ({
  schedule: {},
  root: {
    backgroundColor: theme.palette.surface,
    paddingBottom: theme.spacing(3),
    '&$schedule': {
      paddingBottom: 0,
    },
  },
  container: {
    paddingTop: theme.spacing(2),
  },
  sticky: {
    position: 'sticky',
    top: 200,
  },
  fabIcon: {
    marginRight: theme.spacing(1),
  },
  fab: {
    fontWeight: 'bold',
    minWidth: APP_CONTENT_HEADER_MIN_WIDTH,
  },
  fabToolbarContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(0),
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  fullWidth: {
    width: '100%',
  },
});

/* -----------------Type declarations--------------- */
type Classes = {
  root: string,
  container: string,
  fabToolbarContainer: string,
  fab: string,
  fabIcon: string,
  sticky: string,
  schedule: string,
  fullWidth: string,
};

/* -----------------Props--------------- */
type Props = {
  classes: Classes,
  // single valued
  content: Content,
  metadata: ContentMetaData,
  smartFolderType: FolderTypes,
  variant?: ContentVariants,
  numSelected: number,
  shouldShowFab: boolean,
  initSelectedMap: *,
  inReorderingMode: boolean,
  loading?: boolean,
  scheduleSecondaryText?: string,
  breadcrumbs: TBreadcrumbs[],
  contentInputs: ContentInputs[],
  // Actions
  renderContentMenu?: () => void,
  isFetchingIds: boolean,
  handleActions?: (type: string) => void,
  handleAddToFolder?: () => void,
  handleScheduleActions?: *,
  hasAccess: boolean,
  handleStudySession?: () => void
 };

/* -----------------Component--------------- */
const AppContentHeaderCommon = (props: Props) => {
  const {
    content,
    inReorderingMode,
    isFetchingIds,
    loading,
    renderContentMenu,
    handleStudySession,
    handleActions,
    numSelected,
    hasAccess,
    scheduleSecondaryText,
    breadcrumbs,
    contentInputs,
    content: {
      contentType,
      accessType,
      itemsCount,
      contentsCount,
      contentsItemsCount,
      owner,
      bundleId,
      numericId,
      isFlashCardsOnly,
      icon: contentIcon,
    },
    metadata,
    metadata: {
      isFavorited,
    },
    variant,
    shouldShowFab,
    smartFolderType,
    handleScheduleActions,
    initSelectedMap,
    classes,
    handleAddToFolder,
  } = props;
  const isPremium = accessType === contentAccessType.PREMIUM;

  const isAuthenticated = authenticated(key.loggedIn);

  if (window && window.zE) {
    window.zE(() => {
      if (!isAuthenticated) { window.zE.hide(); } else {
        window.zE.show();
      }
    });
  }
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);

  const [sessionLoaderProp, setSessionLoaderProp] = useState({
    visible: false,
    selectedMode: null,
  });
  const handleCloseCreateSessionLoader = () => {
    setSessionLoaderProp({
      visible: false,
    });
  };
  const [authDialogOpen, setAuthDialogOpen] = React.useState(false);
  const [bundle, setBundleDetails] = React.useState(null);
  const fetchPremiumInfo = React.useCallback(async () => {
    if (isPremium && bundleId) {
      const bundleDetails = await fetchPremiumContentDetails(bundleId, env);
      setBundleDetails(bundleDetails);
    }
  }, [isPremium, bundleId]);

  React.useEffect(() => {
    fetchPremiumInfo();
  }, [fetchPremiumInfo]);

  const handleClose = () => {
    setAuthDialogOpen(false);
  };
  const {
    asPath,
  } = useRouter();

  const {
    description,
    title,
  } = getContentTitleAndDescription(smartFolderType, content);

  const [showInfo, setShowInfo] = useState(false);
  const contentItemsCount = React.useMemo(() => (contentsCount || itemsCount
    ? getContentItemsCount(contentsCount,
      contentsItemsCount, itemsCount) : null), [contentsCount, contentsItemsCount, itemsCount]);

  const isSchedule = variant === 'schedule';

  const finalItemsCount = React.useMemo(() => (contentItemsCount
    ? getItemsCount(
      isSchedule ? 'FOLDER' : contentType,
      contentItemsCount,
    ) : 0), [contentType, contentItemsCount, isSchedule]);

  const selected = numSelected > 0;

  const getDescription = () => (isSchedule ? null : description);

  const isSmartFolder = !isEmpty(smartFolderType);

  const handleAppbarAction = React.useCallback((type: string) => {
    switch (type) {
      case 'info':
        if (handleScheduleActions) {
          handleScheduleActions('showInfo');
        } else {
          setShowInfo(true);
        }
        break;
      default:
        if (handleActions) {
          handleActions(type);
        }
    }
  }, [handleActions, handleScheduleActions]);

  const handleFabClick = () => {
    if (isAuthenticated && hasAccess && handleStudySession) {
      if (handleActions) handleActions('discard');
      handleStudySession();
    } else if (bundleId) {
      setPremiumModalOpen(true);
    } else setAuthDialogOpen(true);
  };

  const renderContentInfo = () => {
    const modifiedContent = {
      ...content,
      name: title,
      description,
    };
    return showInfo ? (
      <ContentInfoDialog
        open={showInfo}
        handleClose={() => setShowInfo(false)}
        content={modifiedContent}
        smartFolderType={smartFolderType}
      />
    ) : null;
  };

  const hasNoContents = !itemsCount && (contentItemsCount && !contentItemsCount.numFacts
      && !contentItemsCount.numQuestions);

  const disableFab = hasNoContents || isFetchingIds || (isPremium && isEmpty(bundleId)) || loading;

  const isSm = useMediaQuery(cTheme => cTheme.breakpoints.down('sm'));
  const isXs = useMediaQuery(cTheme => cTheme.breakpoints.down('xs'));

  const renderFab = () => {
    const studyFabs = {
      study: {
        icon: <Play className={classes.fabIcon} />,
        cta: selected ? 'STUDY SELECTED' : 'STUDY',
        mode: learningMode.match,
      },
      flip: {
        icon: <CardsOutline className={classes.fabIcon} />,
        cta: 'Flashcards',
        mode: learningMode.flip,
      },
      quiz: {
        icon: <FormatListChecks className={classes.fabIcon} />,
        cta: 'Multi-Choice',
        mode: learningMode.quiz,
      },
      match: {
        icon: <Checkerboard className={classes.fabIcon} />,
        cta: 'Matching',
        mode: learningMode.match,
      },
    };

    const {
      study, flip, quiz, match,
    } = studyFabs;

    const getCTAs = () => {
      if (!isAuthenticated && contentType === 'STUDY_SET') {
        if (isFlashCardsOnly) {
          return [flip];
        }
        return [flip, quiz, match];
      }
      return [study];
    };

    const ctas = getCTAs();

    const FabStudy = () => (
      <Grid
        container
        spacing={2}
        direction={isXs ? 'column' : 'row'}
        justifyContent={isSm ? 'center' : 'flex-start'}
        alignItems="center"
      >
        {ctas.map(({ icon, cta, mode }) => (
          <Grid item container={isXs}>
            <Fab
              key={cta}
              variant="extended"
              size="large"
              color="primary"
              disabled={disableFab}
              onClick={() => {
                trackEvent('study fab clicked', {
                  'Call to action': cta,
                });
                if (!isAuthenticated && contentType === 'STUDY_SET' && !isPremium) {
                  setSessionLoaderProp({
                    visible: true,
                    selectedMode: mode,
                  });
                } else {
                  handleFabClick();
                }
              }}
              classes={{
                root: clsx(
                  classes.fab,
                  classes.sticky,
                  isXs && classes.fullWidth,
                ),
              }}
              elevation={2}
            >
              {isFetchingIds ? <CircularProgress color="secondary" size={24} /> : (
                <>
                  {icon}
                  {cta}
                </>
              )}
            </Fab>
          </Grid>
        ))}
      </Grid>
    );

    return (
      <div className={classes.fabToolbarContainer}>
        <FabStudy />
      </div>
    );
  };

  const contentIconUrl = getContentIconUrl(contentIcon);

  const handleClosePremiumModal = () => {
    setPremiumModalOpen(false);
  };
  return (
    <>
      {hasAccess !== undefined && !hasAccess && bundleId && isPremium ? (
        <BannerPremiumContainer
          bundleNumericId={bundleId}
          contentType={contentType}
          showPremiumModal={premiumModalOpen}
          redirectUrl={asPath}
          handleClosePremiumModal={handleClosePremiumModal}
        />
      ) : null}
      <Appbar
        title={title}
        numSelected={numSelected}
        handleAppbarAction={handleAppbarAction}
        favorited={isFavorited}
        contentType={contentType}
        isAuthenticated={isAuthenticated}
        variant={variant}
        breadcrumbs={breadcrumbs}
        showSelectAll={initSelectedMap
          && initSelectedMap.size !== numSelected}
        inReorderingMode={inReorderingMode}
        renderContentMenu={renderContentMenu ? () => renderContentMenu() : () => null}
        handleAddToFolder={handleAddToFolder}
      />
      {!inReorderingMode ? (
        <Paper
          classes={{ root: clsx(classes.root, isSchedule && classes.schedule) }}
          elevation={0}
          square
        >
          <Container
            maxWidth="lg"
            classes={{ root: classes.container }}
          >
            <LearningContentHero
              type={contentType}
              title={smartFolderType || title}
              itemCount={finalItemsCount}
              premium={isPremium}
              owner={owner}
              bundleDetails={bundle}
              metadata={metadata}
              contentIconUrl={contentIconUrl}
              folderItemsCount={contentsItemsCount}
              description={getDescription()}
              isSmartFolder={isSmartFolder}
              scheduleSecondaryText={scheduleSecondaryText}
              loading={loading}
              isAuthenticated={isAuthenticated}
              numericId={numericId}
            />
            {shouldShowFab ? renderFab() : null}
          </Container>
        </Paper>
      ) : null}
      {renderContentInfo()}
      {authDialogOpen ? (
        <DialogAuth
          handleClose={handleClose}
          initialView="login"
          open={authDialogOpen}
          dialogTitle="Login to study"
        />
      ) : null}
      {sessionLoaderProp.visible ? (
        <StudySessionUnAuthLoader
          handleClose={handleCloseCreateSessionLoader}
          contentInputs={contentInputs}
          distribution={metadata.distribution}
          learningGoal={sessionLoaderProp.selectedMode}
        />
      ) : null}
    </>
  );
};

export default withStyles(styles)(AppContentHeaderCommon);
